import { useMemo } from "react";

import { ProtectedLink } from "@impero-com/common/components/protectedLink/index";
import { TFunction, useTranslation } from "@impero-com/common/utils/i18n";
import { assertNever } from "@impero-com/common/utils/obj";
import {
    TST_BLESSED_ID_COLUMN_SELECTION,
    TST_COLUMN_SIZE,
    TST_DEFAULT_PAGE_SIZE,
} from "@impero-com/ui";
import {
    usePresetTableSearchParams,
    ZodCodecMapToType,
} from "@impero-com/ui/domain";
import * as Sentry from "@sentry/react";
import {
    createColumnHelper,
    getCoreRowModel,
    getFacetedMinMaxValues,
    getFilteredRowModel,
    getSortedRowModel,
    useReactTable,
    getFacetedUniqueValues,
    getPaginationRowModel,
    Column,
} from "@tanstack/react-table";
import { Checkbox } from "antd";

import { EntityForUserListDto, StandardAccessRight } from "api/generated/model";
import { TSTValidMultipleOptionFilterSelect } from "components/table/tsTable/TSTValidMultipleOptionFilterSelect";
import { WebRoute } from "routes";
import { createFilterFnWithMode } from "utils/table/createFilterFnWithMode";
import { createFilterHelpersGetter } from "utils/table/getTypedFilterValue";
import { tstArrIncludesSomeFilterFn } from "utils/table/tstArrIncludesSomeFilterFn";

import {
    ENTITIES_LIST_COLUMN_IDS,
    ENTITIES_TABLE_STATE_STORAGE_KEY,
    entitiesListColumnFiltersCodecMap,
    entitiesListTableDefaultValues,
} from "./consts";
export interface EntityRow {
    id: number;
    name: string;
    description: string | null;
    riskAccessRight: StandardAccessRight | null;
    hasAccess: boolean;
}

export function useUserEntitiesListTable(data: EntityForUserListDto) {
    const { t } = useTranslation();

    const tableData = useMemo<EntityRow[]>(
        () =>
            data.content
                .filter(({ resource }) => resource.riskAccessRight !== null)
                .map(({ resource: { entity, riskAccessRight }, actions }) => ({
                    id: entity.id,
                    name: entity.name,
                    description: entity.description ?? null,
                    riskAccessRight: riskAccessRight ?? null,
                    hasAccess: actions.view,
                })),
        [data]
    );
    const notificationLabel = t("Table_RemovedFiltersWarning");

    const stateAndOnChanges = usePresetTableSearchParams(
        ENTITIES_TABLE_STATE_STORAGE_KEY,
        entitiesListTableDefaultValues,
        entitiesListColumnFiltersCodecMap,
        notificationLabel,
        Sentry.captureException
    );
    const columns = buildColumns(t);
    const table = useReactTable({
        ...stateAndOnChanges,
        data: tableData,
        columns,
        getRowId: (row) => row.id.toString(),
        initialState: {
            ...stateAndOnChanges.initialState,
            pagination: {
                pageSize: TST_DEFAULT_PAGE_SIZE,
                pageIndex: 0,
            },
        },
        enableSortingRemoval: false,
        getCoreRowModel: getCoreRowModel<EntityRow>(),
        getSortedRowModel: getSortedRowModel<EntityRow>(),
        getFilteredRowModel: getFilteredRowModel<EntityRow>(),
        getFacetedMinMaxValues: getFacetedMinMaxValues<EntityRow>(),
        getFacetedUniqueValues: getFacetedUniqueValues<EntityRow>(),
        getPaginationRowModel: getPaginationRowModel(),
    });

    const filterComponent = (column: Column<EntityRow>) =>
        getFilterComponent(t, column);

    return {
        table,
        getFilterComponent: filterComponent,
        tableId: ENTITIES_TABLE_STATE_STORAGE_KEY,
    };
}

const columnHelper = createColumnHelper<EntityRow>();
function buildColumns(t: TFunction) {
    return [
        columnHelper.display({
            id: TST_BLESSED_ID_COLUMN_SELECTION,
            header: (info) => {
                return (
                    <Checkbox
                        checked={info.table.getIsAllPageRowsSelected()}
                        indeterminate={info.table.getIsSomePageRowsSelected()}
                        onChange={info.table.getToggleAllRowsSelectedHandler()}
                    />
                );
            },
            size: TST_COLUMN_SIZE.CHECKBOX,
            meta: { fixedWidth: true },
            cell: (info) => {
                const onClick = () => {
                    info.row.toggleSelected();
                };
                return (
                    <Checkbox
                        checked={info.row.getIsSelected()}
                        onClick={onClick}
                    />
                );
            },
        }),
        columnHelper.accessor((row) => row.name, {
            id: ENTITIES_LIST_COLUMN_IDS.NAME,
            cell: (info) => (
                // Entity view page has a router which decides the route based on the entity kind (risk or control)
                <ProtectedLink
                    to={`${WebRoute.ENTITIES}/${info.row.original.id}`}
                    hasAccess={info.row.original.hasAccess}
                >
                    {info.getValue()}
                </ProtectedLink>
            ),
            enableSorting: true,
            sortingFn: "alphanumeric",
            enableColumnFilter: true,
            filterFn: createFilterFnWithMode<EntityRow, string[]>(
                (row, value) =>
                    tstArrIncludesSomeFilterFn(
                        row,
                        ENTITIES_LIST_COLUMN_IDS.NAME,
                        value
                    )
            ),
            header: () => t("AccessAndAssignment_Entities_Entity"),
            size: TST_COLUMN_SIZE.XL,
        }),
        columnHelper.accessor((row) => row.description ?? "", {
            id: ENTITIES_LIST_COLUMN_IDS.DESCRIPTION,
            cell: (info) => <span>{info.getValue()}</span>,
            enableSorting: true,
            sortingFn: "alphanumeric",
            enableColumnFilter: false,
            header: () => t("AccessAndAssignment_Entities_Description"),
            size: TST_COLUMN_SIZE.XL,
        }),
        columnHelper.accessor((row) => row.riskAccessRight, {
            id: ENTITIES_LIST_COLUMN_IDS.ACCESS_RIGHT,
            cell: (info) => <span>{info.getValue() ?? ""}</span>,
            enableSorting: true,
            sortingFn: "alphanumeric",
            enableColumnFilter: true,
            filterFn: createFilterFnWithMode<EntityRow, string[]>(
                (row, value) =>
                    tstArrIncludesSomeFilterFn(
                        row,
                        ENTITIES_LIST_COLUMN_IDS.ACCESS_RIGHT,
                        value
                    )
            ),
            header: () => t("AccessAndAssignment_Entities_AccessRight"),
            size: TST_COLUMN_SIZE.M,
        }),
    ];
}

type EntitiesListColumnFilters = ZodCodecMapToType<
    typeof entitiesListColumnFiltersCodecMap
>;

type EntitiesFilterableColumnId = keyof EntitiesListColumnFilters;

function getFilterComponent(t: TFunction, column: Column<EntityRow>) {
    const getFilterHelpers =
        createFilterHelpersGetter<EntitiesListColumnFilters>(column);
    const columnId = column.id as EntitiesFilterableColumnId;

    switch (columnId) {
        case ENTITIES_LIST_COLUMN_IDS.NAME: {
            const { value, setFilterValue } = getFilterHelpers(columnId);
            const values: Map<string, number> = column.getFacetedUniqueValues();
            const options = [...values.keys()].map((entity) => ({
                label: entity,
                value: entity,
            }));
            return (
                <TSTValidMultipleOptionFilterSelect
                    value={value}
                    column={column}
                    options={options}
                    placeholder={t("AccessAndAssignment_Entities_Entity")}
                    setFilterValue={setFilterValue}
                />
            );
        }
        case ENTITIES_LIST_COLUMN_IDS.ACCESS_RIGHT: {
            const { value, setFilterValue } = getFilterHelpers(columnId);
            const values: Map<string, number> = column.getFacetedUniqueValues();
            const options = [...values.keys()].map((accessRight) => ({
                label: accessRight,
                value: accessRight,
            }));
            return (
                <TSTValidMultipleOptionFilterSelect
                    value={value}
                    column={column}
                    options={options}
                    placeholder={t("AccessAndAssignment_Entities_AccessRight")}
                    setFilterValue={setFilterValue}
                />
            );
        }
        default:
            return assertNever(columnId);
    }
}
